import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SALUTATIONS, TITLES, GENDERS, MONTHS_DE } from './constants';
import { usePlzCities, CitySuggestions, PhoneField } from './shared';
import { cityKeptOrFilled } from '@/lib/plzOrt';

export default function StepGuardian({ form, setField, title, invalid }) {
  const g = form.guardian;
  const ec = (k) => (invalid(k) ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : '');
  const plzCities = usePlzCities(g.same_as_payer || g.address_same ? '' : g.postal_code);
  useEffect(() => {
    const next = cityKeptOrFilled(g.city, plzCities);
    if (next !== (g.city ?? '')) setField('guardian', 'city', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plzCities, g.city]);
  return (
    <div className="space-y-5" data-testid="public-registration-step-guardian">
      <h2 className="text-lg md:text-lg font-semibold text-slate-900">{title}</h2>

      <RadioGroup
        value={g.same_as_payer ? 'same' : 'different'}
        onValueChange={(v) => setField('guardian', 'same_as_payer', v === 'same')}
        className="space-y-1"
      >
        <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-guardian-same-row">
          <RadioGroupItem value="same" data-testid="public-registration-guardian-same" />
          <span className="text-slate-800">Zahlungspflichtige:r ist auch erziehungsberechtigt</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-guardian-different-row">
          <RadioGroupItem value="different" data-testid="public-registration-guardian-different" />
          <span className="text-slate-800">Abweichende Person eingeben</span>
        </label>
      </RadioGroup>

      {!g.same_as_payer && (
        <div className="border border-slate-200 rounded-lg" data-testid="public-registration-guardian-form">
          <div className="bg-slate-50 border-b border-slate-200 rounded-t-lg px-4 py-2.5 text-sm font-medium text-slate-700">
            Erziehungsberechtigte:r
          </div>
          <div className="p-4 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Anrede</Label>
                <Select value={g.salutation} onValueChange={(v) => setField('guardian', 'salutation', v)}>
                  <SelectTrigger data-testid="public-registration-guardian-salutation"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
                  <SelectContent>
                    {SALUTATIONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Titel</Label>
                <Select value={g.title} onValueChange={(v) => setField('guardian', 'title', v)}>
                  <SelectTrigger data-testid="public-registration-guardian-title"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
                  <SelectContent>
                    {TITLES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={invalid('guardian.first_name') ? 'text-red-600' : ''}>Vorname *</Label>
                <Input placeholder="Max" className={ec('guardian.first_name')} value={g.first_name} onChange={(e) => setField('guardian', 'first_name', e.target.value)} data-testid="public-registration-guardian-firstname" />
              </div>
              <div>
                <Label className={invalid('guardian.last_name') ? 'text-red-600' : ''}>Nachname *</Label>
                <Input placeholder="Mustermann" className={ec('guardian.last_name')} value={g.last_name} onChange={(e) => setField('guardian', 'last_name', e.target.value)} data-testid="public-registration-guardian-lastname" />
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer" data-testid="public-registration-guardian-addresssame-row">
              <Switch
                checked={g.address_same}
                onCheckedChange={(v) => setField('guardian', 'address_same', !!v)}
                data-testid="public-registration-guardian-addresssame-toggle"
              />
              <span className="text-slate-800">Adresse von zahlungspflichtiger Person übernehmen</span>
            </label>

            {!g.address_same && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <Label className={invalid('guardian.street') ? 'text-red-600' : ''}>Straße *</Label>
                    <Input className={ec('guardian.street')} value={g.street} onChange={(e) => setField('guardian', 'street', e.target.value)} data-testid="public-registration-guardian-street" />
                  </div>
                  <div>
                    <Label className={invalid('guardian.house_number') ? 'text-red-600' : ''}>Hausnr. *</Label>
                    <Input inputMode="numeric" className={ec('guardian.house_number')} value={g.house_number} onChange={(e) => setField('guardian', 'house_number', e.target.value.replace(/\D/g, ''))} data-testid="public-registration-guardian-housenumber" />
                  </div>
                  <div>
                    <Label>Zusatz</Label>
                    <Input value={g.address_extra} onChange={(e) => setField('guardian', 'address_extra', e.target.value)} data-testid="public-registration-guardian-addressextra" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label className={invalid('guardian.postal_code') ? 'text-red-600' : ''}>Postleitzahl *</Label>
                    <Input inputMode="numeric" maxLength={5} className={ec('guardian.postal_code')} value={g.postal_code} onChange={(e) => setField('guardian', 'postal_code', e.target.value.replace(/\D/g, '').slice(0, 5))} data-testid="public-registration-guardian-postal" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className={invalid('guardian.city') ? 'text-red-600' : ''}>Ort *</Label>
                    <Input className={ec('guardian.city')} value={g.city} onChange={(e) => setField('guardian', 'city', e.target.value)} data-testid="public-registration-guardian-city" />
                    <CitySuggestions cities={plzCities} current={g.city} onPick={(c) => setField('guardian', 'city', c)} testPrefix="public-registration-guardian" />
                  </div>
                  <div>
                    <Label>Ortsteil</Label>
                    <Input value={g.district} onChange={(e) => setField('guardian', 'district', e.target.value)} data-testid="public-registration-guardian-district" />
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Geschlecht</Label>
                <Select value={g.gender} onValueChange={(v) => setField('guardian', 'gender', v)}>
                  <SelectTrigger data-testid="public-registration-guardian-gender"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
                  <SelectContent>
                    {GENDERS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Geburtstag</Label>
                <Select value={g.birth_day} onValueChange={(v) => setField('guardian', 'birth_day', v)}>
                  <SelectTrigger data-testid="public-registration-guardian-birthday"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monat</Label>
                <Select value={g.birth_month} onValueChange={(v) => setField('guardian', 'birth_month', v)}>
                  <SelectTrigger data-testid="public-registration-guardian-birthmonth"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {MONTHS_DE.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jahr</Label>
                <Select value={g.birth_year} onValueChange={(v) => setField('guardian', 'birth_year', v)}>
                  <SelectTrigger data-testid="public-registration-guardian-birthyear"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 100 }, (_, i) => String(new Date().getFullYear() - 18 - i)).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={invalid('guardian.email') ? 'text-red-600' : ''}>E-Mail</Label>
                <Input type="email" placeholder="max.mustermann@beispiel.de" className={ec('guardian.email')} value={g.email} onChange={(e) => setField('guardian', 'email', e.target.value)} data-testid="public-registration-guardian-email" />
              </div>
              <div>
                <Label className={invalid('guardian.phone') ? 'text-red-600' : ''}>Rufnummer</Label>
                <PhoneField value={g.phone} onChange={(v) => setField('guardian', 'phone', v)} error={invalid('guardian.phone')} testId="public-registration-guardian-phone" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
